// [FICHA A/B 28/09] Aba "Ficha A/B" — leitura do experimento da ficha de liquidação.
// Regra da aba: mostrar o que foi medido, sem juízo. Nenhuma cor de "bom/ruim", nenhum
// adjetivo; o único destaque é o FATO estatístico de o IC95 não cruzar zero. Quem decide
// lê o intervalo, não a diferença.
// Backend: GET /ficha-ab/metrics?desde=&ate=  (&csv=1 devolve por_org em CSV).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Text, Title } from '@tremor/react';
import { ArrowUpDown, Download, FlaskConical, RefreshCw, Search } from 'lucide-react';
import {
  extractErrorMessage,
  getFichaAbCsv,
  getFichaAbMetrics,
} from '@/lib/api';

// ── Tipos (payload de GET /ficha-ab/metrics — routers/ficha_ab.py) ───────────

type FichaAbVariavelTipo = 'taxa' | 'media' | 'soma';

interface FichaAbBraco {
  valor: number | null;
  n: number | null;
}

/** CUPED: a mesma diferença, descontando o que a org JÁ fazia nos 60 dias anteriores a `desde`.
 *  `aplicavel=false` (ex.: todas as orgs novas ⇒ covariável constante) ⇒ é o valor cru, sem ajuste. */
interface FichaAbCuped {
  teste: FichaAbBraco;
  controle: FichaAbBraco;
  diff: number | null;
  ic95: [number, number] | null;
  p: number | null;
  q: number | null;
  theta: number | null;
  /** fração (0–1) da variância que a covariável absorveu. */
  reducao_variancia: number | null;
  aplicavel: boolean;
  covariavel: string;
}

interface FichaAbVariavel {
  id: string;
  rotulo: string;
  tipo: FichaAbVariavelTipo;
  unidade: string | null;
  teste: FichaAbBraco;
  controle: FichaAbBraco;
  diff: number | null;
  /** [lo, hi] do IC95 da diferença; null quando o backend não consegue estimar. */
  ic95: [number, number] | null;
  p: number | null;
  /** q-value de Benjamini-Hochberg DENTRO do bloco. O marcador da aba é q < bh.q_limiar. */
  q: number | null;
  /** só nas métricas por org (média); null nas taxas. */
  cuped: FichaAbCuped | null;
}

interface FichaAbBayesMetrica {
  p_teste_maior: number | null;
  expected_loss_teste: number | null;
  expected_loss_controle: number | null;
  media_post_teste: number | null;
  media_post_controle: number | null;
  braco_menor_perda: 'teste' | 'controle' | null;
  perda_do_escolhido: number | null;
  atinge_limiar: boolean | null;
  limiar: number;
  prior: string;
  teste: { k: number; n: number };
  controle: { k: number; n: number };
}

interface FichaAbBayes {
  prior: string;
  limiar: number;
  nota: string;
  metricas: Record<string, FichaAbBayesMetrica>;
}

interface FichaAbEtapa {
  id: string;
  rotulo: string;
  orgs: number;
  pct_dos_elegiveis: number | null;
}

interface FichaAbCondicional {
  orgs: number;
  compraram: number;
  taxa: number | null;
}

interface FichaAbFunil {
  etapas: FichaAbEtapa[];
  compra_condicionada: {
    teste_abriu: FichaAbCondicional;
    teste_nao_abriu: FichaAbCondicional;
    controle_elegiveis: FichaAbCondicional;
    abriu_vs_controle: Omit<FichaAbVariavel, 'id' | 'rotulo' | 'tipo' | 'unidade' | 'q' | 'cuped'>;
    aviso: string;
  };
}

interface FichaAbBloco {
  id: string;
  rotulo: string;
  nota: string;
  primario: boolean;
  grupos: { teste: { orgs: number | null }; controle: { orgs: number | null } };
  variaveis: FichaAbVariavel[];
  bayes?: FichaAbBayes;
  funil?: FichaAbFunil;
}

interface FichaAbExposicao {
  orgs_teste_com_ficha_aberta: number | null;
  pct: number | null;
  fichas_abertas: number | null;
  edicoes_salvas: number | null;
  recalcs_via_ficha: number | null;
}

interface FichaAbOrgLinha {
  org_id: number;
  nome: string | null;
  grupo: 'teste' | 'controle';
  atribuido_em: string | null;
  casos: number | null;
  completed: number | null;
  erros: number | null;
  recalcs: number | null;
  recalcs_via_ficha: number | null;
  fichas_abertas: number | null;
  edicoes: number | null;
  creditos_reprocesso: number | null;
  creditos_total: number | null;
  compras: number | null;
  valor_compras_brl: number | null;
  recompra: boolean | number | null;
  ativo_d7: boolean | number | null;
  ativo_d14: boolean | number | null;
  ativo_d30: boolean | number | null;
  estornos: number | null;
  elegivel?: boolean | number | null;
  pre_casos?: number | null;
  pre_completed?: number | null;
  pre_creditos_total?: number | null;
  pre_compras?: number | null;
  pre_valor_compras_brl?: number | null;
}

interface FichaAbMetrics {
  disponivel?: boolean;
  erro?: string;
  desde: string | null;
  ate: string | null;
  grupos: { teste: { orgs: number | null }; controle: { orgs: number | null } };
  variaveis: FichaAbVariavel[];
  exposicao: FichaAbExposicao;
  por_org: FichaAbOrgLinha[];
  /** [METRICAS 08/10] elegíveis (primário) + ITT diluído. Ausente ⇒ payload antigo. */
  blocos?: FichaAbBloco[];
  cuped?: { dias: number; pre_desde: string; pre_ate: string; covariaveis: Record<string, string> };
  bh?: { q_limiar: number; nota: string };
}

// ── Formatação ───────────────────────────────────────────────────────────────

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function ehBrl(v: FichaAbVariavel): boolean {
  const u = (v.unidade || '').toLowerCase();
  return u === 'brl' || u === 'r$' || u.includes('brl');
}

function num(valor: number, minCasas = 1, maxCasas = 2): string {
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: minCasas,
    maximumFractionDigits: maxCasas,
  });
}

/** soma = contagem: inteiro quando é inteiro; média = 1–2 casas. */
function numPorTipo(valor: number, tipo: FichaAbVariavelTipo): string {
  if (tipo === 'soma') return num(valor, 0, Number.isInteger(valor) ? 0 : 2);
  return num(valor, 1, 2);
}

/** Valor de um braço, no formato do tipo da variável. */
function fmtValor(valor: number | null | undefined, v: FichaAbVariavel): string {
  if (valor == null || Number.isNaN(valor)) return '—';
  if (v.tipo === 'taxa') return `${num(valor * 100, 1)}%`;
  if (ehBrl(v)) return BRL.format(valor);
  const s = numPorTipo(valor, v.tipo);
  return v.unidade && v.unidade !== '%' ? `${s} ${v.unidade}` : s;
}

/** Diferença (teste − controle). Taxa vira pontos percentuais — a unidade honesta
 *  de uma diferença de taxas. */
function fmtDiff(valor: number | null | undefined, v: FichaAbVariavel): string {
  if (valor == null || Number.isNaN(valor)) return '—';
  const sinal = valor > 0 ? '+' : valor < 0 ? '−' : '';
  const abs = Math.abs(valor);
  if (v.tipo === 'taxa') return `${sinal}${num(abs * 100, 1)} pp`;
  if (ehBrl(v)) return `${sinal}${BRL.format(abs)}`;
  return `${sinal}${numPorTipo(abs, v.tipo)}`;
}

function fmtIc95(ic: [number, number] | null | undefined, v: FichaAbVariavel): string {
  if (!ic || ic.length !== 2 || ic[0] == null || ic[1] == null) return '—';
  return `[${fmtDiff(ic[0], v)} · ${fmtDiff(ic[1], v)}]`;
}

function fmtP(p: number | null | undefined): string {
  if (p == null || Number.isNaN(p)) return '—';
  if (p < 0.001) return '< 0,001';
  return p.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function fmtQ(q: number | null | undefined): string {
  if (q == null || Number.isNaN(q)) return '—';
  if (q < 0.001) return '< 0,001';
  return q.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** Fato, não juízo: o intervalo de 95% fica todo de um lado do zero. */
function ic95ForaDoZero(ic: [number, number] | null | undefined): boolean {
  if (!ic || ic[0] == null || ic[1] == null) return false;
  const [lo, hi] = ic;
  return (lo > 0 && hi > 0) || (lo < 0 && hi < 0);
}

/** [METRICAS 08/10] O marcador da aba. Antes era "IC95 ∌ 0" (equivalente a p<0,05); com 14+
 *  variáveis por leitura isso rende ~1 falso positivo por leitura só pelo número de testes.
 *  Agora exige o q de Benjamini-Hochberg abaixo do limiar que o backend declara (0,10). */
function passaBh(q: number | null | undefined, limiar: number): boolean {
  return q != null && !Number.isNaN(q) && q < limiar;
}

/** Fração 0–1 → "72%". */
function fmtFracao(v: number | null | undefined, casas = 0): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${num(v * 100, casas, casas)}%`;
}

/** Expected loss / taxa em pontos percentuais. */
function fmtPp(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${num(v * 100, 2, 2)} pp`;
}

function fmtN(n: number | null | undefined): string {
  return n == null ? 'n —' : `n ${n.toLocaleString('pt-BR')}`;
}

function fmtInt(valor: number | null | undefined): string {
  return valor == null || Number.isNaN(valor) ? '—' : Math.round(valor).toLocaleString('pt-BR');
}

function fmtPct(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return '—';
  // O backend pode mandar 0–1 ou 0–100; acima de 1 já é percentual.
  const pct = valor <= 1 ? valor * 100 : valor;
  return `${num(pct, 1)}%`;
}

function fmtData(valor: string | null | undefined): string {
  if (!valor) return '—';
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? String(valor).slice(0, 10) : d.toLocaleDateString('pt-BR');
}

function fmtBool(valor: boolean | number | null | undefined): string {
  if (valor == null) return '—';
  if (typeof valor === 'number') return valor ? 'sim' : 'não';
  return valor ? 'sim' : 'não';
}

function boolOrd(valor: boolean | number | null | undefined): number {
  if (valor == null) return -1;
  return (typeof valor === 'number' ? valor > 0 : valor) ? 1 : 0;
}

// ── Colunas do por_org ───────────────────────────────────────────────────────

type OrgCol = {
  key: keyof FichaAbOrgLinha;
  label: string;
  tipo: 'texto' | 'grupo' | 'data' | 'int' | 'brl' | 'bool';
};

const ORG_COLS: OrgCol[] = [
  { key: 'nome', label: 'Org', tipo: 'texto' },
  { key: 'grupo', label: 'Grupo', tipo: 'grupo' },
  { key: 'atribuido_em', label: 'Atribuído em', tipo: 'data' },
  { key: 'elegivel', label: 'Elegível', tipo: 'bool' },
  { key: 'casos', label: 'Casos', tipo: 'int' },
  { key: 'completed', label: 'Completed', tipo: 'int' },
  { key: 'erros', label: 'Erros', tipo: 'int' },
  { key: 'recalcs', label: 'Recalcs', tipo: 'int' },
  { key: 'recalcs_via_ficha', label: 'Recalcs via ficha', tipo: 'int' },
  { key: 'fichas_abertas', label: 'Fichas abertas', tipo: 'int' },
  { key: 'edicoes', label: 'Edições', tipo: 'int' },
  { key: 'creditos_reprocesso', label: 'Créd. reprocesso', tipo: 'int' },
  { key: 'creditos_total', label: 'Créd. total', tipo: 'int' },
  { key: 'compras', label: 'Compras', tipo: 'int' },
  { key: 'valor_compras_brl', label: 'Valor compras', tipo: 'brl' },
  { key: 'recompra', label: 'Recompra', tipo: 'bool' },
  { key: 'ativo_d7', label: 'Ativo D7', tipo: 'bool' },
  { key: 'ativo_d14', label: 'Ativo D14', tipo: 'bool' },
  { key: 'ativo_d30', label: 'Ativo D30', tipo: 'bool' },
  { key: 'estornos', label: 'Estornos', tipo: 'int' },
];

function ehColunaTexto(col: OrgCol): boolean {
  return col.tipo === 'texto' || col.tipo === 'grupo' || col.tipo === 'data';
}

function celulaOrg(linha: FichaAbOrgLinha, col: OrgCol): string {
  const v = linha[col.key];
  switch (col.tipo) {
    case 'texto':
      return (v as string | null) || `org ${linha.org_id}`;
    case 'grupo':
      return String(v ?? '—');
    case 'data':
      return fmtData(v as string | null);
    case 'brl':
      return v == null ? '—' : BRL.format(Number(v));
    case 'bool':
      return fmtBool(v as boolean | number | null);
    default:
      return fmtInt(v as number | null);
  }
}

function ordemOrg(linha: FichaAbOrgLinha, col: OrgCol): number | string {
  const v = linha[col.key];
  switch (col.tipo) {
    case 'texto':
      return ((v as string | null) || '').toLowerCase();
    case 'grupo':
      return String(v ?? '');
    case 'data':
      return v ? String(v) : '';
    case 'bool':
      return boolOrd(v as boolean | number | null);
    default:
      return v == null ? Number.NEGATIVE_INFINITY : Number(v);
  }
}

// ── [METRICAS 08/10] Bloco (elegíveis × ITT), CUPED, q-value, Bayes e funil ──────────

/** Tabela de variáveis de UM bloco. Duas colunas novas:
 *  • "Ajustado (CUPED)" — a diferença descontando o pré-período da própria org (60d antes de
 *    `desde`). Com 90% de orgs antigas, a variância entre orgs é "escritório grande × pequeno",
 *    não o braço; a covariável absorve isso. `aplicavel=false` ⇒ mostra "—" e o motivo no title.
 *  • "q (BH)" — o q-value do bloco. O marcador passou a ser q < limiar, não IC95 ∌ 0. */
function TabelaVariaveis({ vars, qLimiar }: { vars: FichaAbVariavel[]; qLimiar: number }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-4 text-left font-medium">Variável</th>
            <th className="py-2 pr-4 text-right font-medium">Teste</th>
            <th className="py-2 pr-4 text-right font-medium">Controle</th>
            <th className="py-2 pr-4 text-right font-medium">Diferença</th>
            <th className="py-2 pr-4 text-right font-medium">IC95</th>
            <th className="py-2 pr-4 text-right font-medium">p</th>
            <th className="py-2 pr-4 text-right font-medium">q (BH)</th>
            <th
              className="py-2 text-right font-medium"
              title="CUPED: mesma diferença, descontando a mesma métrica da org nos 60 dias anteriores a `desde`"
            >
              Ajustado (CUPED)
            </th>
          </tr>
        </thead>
        <tbody>
          {vars.map((v) => {
            const aj = v.cuped;
            const marcado = passaBh(v.q, qLimiar);
            return (
              <tr key={v.id} className="border-b border-slate-900 text-slate-200 hover:bg-slate-800/30">
                <td className="py-2 pr-4">
                  {v.rotulo}
                  <span className="block text-[10px] text-slate-500">
                    {v.id} · {v.tipo}
                  </span>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtValor(v.teste?.valor, v)}
                  <span className="block text-[10px] text-slate-500">{fmtN(v.teste?.n)}</span>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtValor(v.controle?.valor, v)}
                  <span className="block text-[10px] text-slate-500">{fmtN(v.controle?.n)}</span>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtDiff(v.diff, v)}
                  {marcado && (
                    <span
                      className="ml-1.5 rounded border border-slate-600 px-1 py-px align-middle text-[10px] font-medium text-slate-300"
                      title={`q de Benjamini-Hochberg < ${num(qLimiar, 2, 2)} (FDR controlado no bloco)`}
                    >
                      q &lt; {num(qLimiar, 2, 2)}
                    </span>
                  )}
                  {!marcado && ic95ForaDoZero(v.ic95) && (
                    <span
                      className="ml-1.5 text-[10px] text-slate-500"
                      title="IC95 não inclui zero, mas o q de BH não passa do limiar: candidato a falso positivo das 14+ leituras"
                    >
                      IC95 ∌ 0
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums text-slate-400">
                  {fmtIc95(v.ic95, v)}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums text-slate-400">{fmtP(v.p)}</td>
                <td className="py-2 pr-4 text-right tabular-nums text-slate-400">{fmtQ(v.q)}</td>
                <td className="py-2 text-right tabular-nums">
                  {aj == null ? (
                    <span className="text-slate-600" title="CUPED só nas métricas por org (média)">
                      —
                    </span>
                  ) : !aj.aplicavel ? (
                    <span
                      className="text-slate-600"
                      title={`covariável ${aj.covariavel} constante no pré-período (ex.: todas as orgs novas ⇒ X=0): sem theta, nada a ajustar`}
                    >
                      n/a
                    </span>
                  ) : (
                    <>
                      <span className="text-slate-200">{fmtDiff(aj.diff, v)}</span>
                      {passaBh(aj.q, qLimiar) && (
                        <span className="ml-1.5 rounded border border-slate-600 px-1 py-px align-middle text-[10px] text-slate-300">
                          q &lt; {num(qLimiar, 2, 2)}
                        </span>
                      )}
                      <span className="block text-[10px] text-slate-500">
                        {fmtIc95(aj.ic95, v)} · q {fmtQ(aj.q)}
                      </span>
                      <span
                        className="block text-[10px] text-slate-500"
                        title={`theta = ${aj.theta == null ? '—' : num(aj.theta, 2, 3)} · covariável ${aj.covariavel}`}
                      >
                        −{fmtFracao(aj.reducao_variancia)} de variância
                      </span>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {vars.length === 0 && (
            <tr>
              <td colSpan={8} className="py-6 text-center text-sm text-slate-500">
                Nenhuma variável devolvida para este período.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Expected loss bayesiano: a pergunta que decide. "Quanto perco, em pontos de taxa, se eu
 *  escolher este braço e ele for o pior." O limiar é PRÉ-registrado no backend (0,5 pp) — não é
 *  escolhido depois de ver o número, que é como o peeking infla o erro tipo I. */
function CardBayes({ bayes }: { bayes: FichaAbBayes }) {
  const linhas = Object.entries(bayes.metricas);
  return (
    <Card>
      <Title className="text-base">Decisão bayesiana (expected loss)</Title>
      <Text className="text-xs">
        {bayes.prior} nas duas taxas. P(teste &gt; controle) é a pergunta que se faz; o expected
        loss é a régua de parada. {bayes.nota}.
      </Text>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-4 text-left font-medium">Métrica</th>
              <th className="py-2 pr-4 text-right font-medium">Teste</th>
              <th className="py-2 pr-4 text-right font-medium">Controle</th>
              <th className="py-2 pr-4 text-right font-medium">P(teste &gt; ctrl)</th>
              <th className="py-2 pr-4 text-right font-medium">Perda se escolher teste</th>
              <th className="py-2 pr-4 text-right font-medium">Perda se escolher controle</th>
              <th className="py-2 text-right font-medium">Limiar {fmtPp(bayes.limiar)}</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(([id, b]) => (
              <tr key={id} className="border-b border-slate-900 text-slate-200">
                <td className="py-2 pr-4">{id}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtFracao(b.media_post_teste, 1)}
                  <span className="block text-[10px] text-slate-500">
                    {b.teste.k}/{b.teste.n}
                  </span>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtFracao(b.media_post_controle, 1)}
                  <span className="block text-[10px] text-slate-500">
                    {b.controle.k}/{b.controle.n}
                  </span>
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {b.p_teste_maior == null ? '—' : fmtFracao(b.p_teste_maior, 1)}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtPp(b.expected_loss_teste)}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {fmtPp(b.expected_loss_controle)}
                </td>
                <td className="py-2 text-right text-xs tabular-nums text-slate-400">
                  {b.atinge_limiar == null
                    ? '—'
                    : b.atinge_limiar
                      ? `atingido (${b.braco_menor_perda}, ${fmtPp(b.perda_do_escolhido)})`
                      : `não atingido (menor perda ${fmtPp(b.perda_do_escolhido)})`}
                </td>
              </tr>
            ))}
            {linhas.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-sm text-slate-500">
                  Sem taxas para decidir neste recorte.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Funil de exposição: onde o grupo teste evapora. Orgs DISTINTAS, não eventos. É o que separa
 *  "a ficha não muda nada" de "quase ninguém chegou a abrir". */
function CardFunil({ funil }: { funil: FichaAbFunil }) {
  const topo = funil.etapas[0]?.orgs ?? 0;
  const cond = funil.compra_condicionada;
  const grupos: Array<{ rotulo: string; dado: FichaAbCondicional }> = [
    { rotulo: 'Teste — abriu a ficha', dado: cond.teste_abriu },
    { rotulo: 'Teste — não abriu', dado: cond.teste_nao_abriu },
    { rotulo: 'Controle elegíveis', dado: cond.controle_elegiveis },
  ];
  return (
    <Card>
      <Title className="text-base">Funil de exposição (grupo teste)</Title>
      <Text className="text-xs">
        Orgs distintas em cada degrau, dentro do recorte de elegíveis. Os degraus NÃO são
        aninhados: comprar não exige ter aberto a ficha, então "compraram" pode passar de
        "recalcularam". Cada barra é % dos elegíveis do teste, não do degrau anterior.
      </Text>
      <div className="mt-3 space-y-1.5">
        {funil.etapas.map((e) => (
          <div key={e.id} className="flex items-center gap-3">
            <div className="w-56 shrink-0 text-xs text-slate-300">{e.rotulo}</div>
            <div className="h-5 flex-1 overflow-hidden rounded bg-slate-900">
              <div
                className="h-full bg-slate-600"
                style={{ width: `${topo > 0 ? (100 * e.orgs) / topo : 0}%` }}
              />
            </div>
            <div className="w-28 shrink-0 text-right text-xs tabular-nums text-slate-300">
              {fmtInt(e.orgs)}
              <span className="ml-1 text-slate-500">
                {e.pct_dos_elegiveis == null ? '' : `(${num(e.pct_dos_elegiveis, 1)}%)`}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 border-t border-slate-800 pt-3">
        <div className="text-xs font-medium text-slate-300">Compra condicionada à exposição</div>
        <Text className="mt-0.5 text-[11px] text-amber-300/80">⚠ {cond.aviso}</Text>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {grupos.map((g) => (
            <div key={g.rotulo} className="rounded-md border border-slate-800 p-2.5">
              <div className="text-[11px] text-slate-400">{g.rotulo}</div>
              <div className="text-lg font-semibold tabular-nums text-slate-100">
                {g.dado.taxa == null ? '—' : fmtFracao(g.dado.taxa, 1)}
              </div>
              <div className="text-[10px] text-slate-500">
                {g.dado.compraram}/{g.dado.orgs} orgs
              </div>
            </div>
          ))}
        </div>
        <Text className="mt-2 text-[11px] text-slate-500">
          Quem abriu a ficha já era mais ativo: compare braço contra braço na tabela de variáveis
          acima — esta linha é descritiva, não causal.
        </Text>
      </div>
    </Card>
  );
}

/** Um bloco inteiro: cabeçalho + n por braço + variáveis (+ Bayes e funil, no primário). */
function BlocoAb({
  bloco,
  qLimiar,
  notaBh,
  cuped,
}: {
  bloco: FichaAbBloco;
  qLimiar: number;
  notaBh: string | null;
  cuped: FichaAbMetrics['cuped'];
}) {
  return (
    <section className="space-y-4">
      <Card className={bloco.primario ? 'border border-slate-700' : 'border border-slate-800/60'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Title className="text-base">
              {bloco.rotulo}
              {!bloco.primario && (
                <span className="ml-2 align-middle text-[10px] font-normal uppercase tracking-wide text-slate-500">
                  secundário
                </span>
              )}
            </Title>
            <Text className="mt-1 max-w-3xl text-xs">{bloco.nota}</Text>
          </div>
          <div className="flex shrink-0 gap-5">
            {(
              [
                { rotulo: 'Teste', orgs: bloco.grupos?.teste?.orgs ?? null },
                { rotulo: 'Controle', orgs: bloco.grupos?.controle?.orgs ?? null },
              ] as const
            ).map((g) => (
              <div key={g.rotulo} className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">{g.rotulo}</div>
                <div className="text-xl font-bold tabular-nums text-slate-100">
                  {fmtInt(g.orgs)} <span className="text-xs font-medium text-slate-400">orgs</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <TabelaVariaveis vars={bloco.variaveis ?? []} qLimiar={qLimiar} />

        <div className="mt-2 space-y-0.5">
          <Text className="text-[11px]">n pequeno: leia o intervalo, não a diferença</Text>
          {notaBh && <Text className="text-[11px]">{notaBh}</Text>}
          {cuped && (
            <Text className="text-[11px] text-slate-500">
              CUPED: covariável = a mesma métrica da org em {cuped.pre_desde} → {cuped.pre_ate} (
              {cuped.dias} dias antes da janela). Org nova entra com X=0.
            </Text>
          )}
        </div>
      </Card>

      {bloco.funil && <CardFunil funil={bloco.funil} />}
      {bloco.bayes && <CardBayes bayes={bloco.bayes} />}
    </section>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────

export function FichaAbPage() {
  // Período aplicado (vai pro backend) × o que está no seletor (rascunho).
  const [periodo, setPeriodo] = useState<{ desde: string; ate: string }>({ desde: '', ate: '' });
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [periodoTocado, setPeriodoTocado] = useState(false);

  const [baixandoCsv, setBaixandoCsv] = useState(false);
  const [erroCsv, setErroCsv] = useState<string | null>(null);

  const [filtroGrupo, setFiltroGrupo] = useState<'todos' | 'teste' | 'controle'>('todos');
  const [busca, setBusca] = useState('');
  const [sortKey, setSortKey] = useState<keyof FichaAbOrgLinha>('casos');
  const [sortDesc, setSortDesc] = useState(true);

  const { data, isLoading, isFetching, error } = useQuery<FichaAbMetrics>({
    queryKey: ['ficha-ab', periodo.desde, periodo.ate],
    queryFn: async () =>
      (await getFichaAbMetrics({ desde: periodo.desde || null, ate: periodo.ate || null })).data,
    refetchInterval: 5 * 60_000,
  });

  // Default do seletor = a janela que o backend devolveu (até o operador mexer).
  useEffect(() => {
    if (periodoTocado || !data) return;
    setDesde(data.desde ? String(data.desde).slice(0, 10) : '');
    setAte(data.ate ? String(data.ate).slice(0, 10) : '');
  }, [data, periodoTocado]);

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar métricas do A/B da ficha')
    : data && data.disponivel === false
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const porOrg = useMemo(() => data?.por_org ?? [], [data]);
  // Elegíveis primeiro (é o recorte primário); o ITT diluído desce para o fim.
  const blocos = useMemo(
    () => [...(data?.blocos ?? [])].sort((a, b) => Number(b.primario) - Number(a.primario)),
    [data],
  );
  const qLimiar = data?.bh?.q_limiar ?? 0.1;

  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const col = ORG_COLS.find((c) => c.key === sortKey) || ORG_COLS[0];
    const filtradas = porOrg.filter((l) => {
      if (filtroGrupo !== 'todos' && l.grupo !== filtroGrupo) return false;
      if (!termo) return true;
      return (l.nome || '').toLowerCase().includes(termo) || String(l.org_id).includes(termo);
    });
    return [...filtradas].sort((a, b) => {
      const va = ordemOrg(a, col);
      const vb = ordemOrg(b, col);
      let cmp: number;
      if (typeof va === 'string' || typeof vb === 'string')
        cmp = String(va).localeCompare(String(vb), 'pt-BR');
      else cmp = va === vb ? 0 : va < vb ? -1 : 1;
      return sortDesc ? -cmp : cmp;
    });
  }, [porOrg, filtroGrupo, busca, sortKey, sortDesc]);

  function ordenarPor(key: keyof FichaAbOrgLinha) {
    if (key === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(key);
      setSortDesc(true);
    }
  }

  function aplicarPeriodo() {
    setPeriodoTocado(true);
    setPeriodo({ desde, ate });
  }

  async function exportarCsv() {
    setBaixandoCsv(true);
    setErroCsv(null);
    try {
      const r = await getFichaAbCsv({ desde: desde || null, ate: ate || null });
      const url = window.URL.createObjectURL(
        new Blob([r.data], { type: 'text/csv;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      const sufixo = [desde, ate].filter(Boolean).join('_') || 'periodo';
      link.download = `ficha_ab_por_org_${sufixo}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErroCsv(extractErrorMessage(e, 'falha ao exportar CSV'));
    } finally {
      setBaixandoCsv(false);
    }
  }

  const orgsTeste = data?.grupos?.teste?.orgs ?? null;
  const orgsControle = data?.grupos?.controle?.orgs ?? null;
  const exp = data?.exposicao;
  const semExperimento =
    !isLoading &&
    !errMsg &&
    (!data ||
      (!data.desde && (orgsTeste ?? 0) === 0 && (orgsControle ?? 0) === 0 && porOrg.length === 0));

  const seletorPeriodo = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
          Desde
        </span>
        <input
          type="date"
          value={desde}
          onChange={(e) => {
            setPeriodoTocado(true);
            setDesde(e.target.value);
          }}
          className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-200"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Até</span>
        <input
          type="date"
          value={ate}
          onChange={(e) => {
            setPeriodoTocado(true);
            setAte(e.target.value);
          }}
          className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-200"
        />
      </label>
      <button
        type="button"
        onClick={aplicarPeriodo}
        disabled={isFetching}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 transition hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        Aplicar
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Title>Ficha A/B</Title>
          {seletorPeriodo}
        </div>
        <Text>
          Experimento da ficha de liquidação, randomizado POR ORG (core/ficha_ab.py). O que foi
          medido na janela {fmtData(data?.desde)} → {fmtData(data?.ate)}, sem adjetivo: valor por
          braço, diferença, IC95, p e o q de Benjamini-Hochberg. Leia o bloco de ELEGÍVEIS (orgs
          com ≥1 cálculo concluído, que é quem podia ver a ficha); o ITT da coorte inteira vem
          depois e é diluído pela exposição. Orgs internas/forçadas têm acesso mas ficam fora da
          conta.
        </Text>
      </header>

      {errMsg && (
        <Card className="border border-red-800/40 bg-red-900/10">
          <Text className="text-red-300">{errMsg}</Text>
          <Text className="mt-1 text-xs text-slate-500">
            Endpoint: <code>GET /ficha-ab/metrics</code> (admin-api :8005).
          </Text>
        </Card>
      )}

      {isLoading && (
        <Card>
          <Text>carregando…</Text>
        </Card>
      )}

      {semExperimento && (
        <Card className="text-center">
          <FlaskConical className="mx-auto mb-2 h-6 w-6 text-slate-600" />
          <Title className="text-base">A/B ainda não ligado</Title>
          <Text className="mx-auto mt-1 max-w-md text-xs">
            Sem <code>FICHA_AB_DESDE</code> e sem orgs atribuídas no período, não há nada medido.
            Esta aba só mostra dados depois que o experimento começa.
          </Text>
        </Card>
      )}

      {!isLoading && !errMsg && !semExperimento && (
        <>
          {/* ── 1. Braços ── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {(
              [
                { rotulo: 'Teste (coorte inteira)', orgs: orgsTeste },
                { rotulo: 'Controle (coorte inteira)', orgs: orgsControle },
              ] as const
            ).map((g) => (
              <Card key={g.rotulo}>
                <Text className="text-xs uppercase tracking-wide">{g.rotulo}</Text>
                <div className="mt-1 text-2xl font-bold tabular-nums text-slate-100">
                  {fmtInt(g.orgs)}{' '}
                  <span className="text-sm font-medium text-slate-400">orgs</span>
                </div>
              </Card>
            ))}
          </div>

          {/* ── 2. Exposição ── */}
          <Card>
            <Title className="text-base">Exposição do grupo teste</Title>
            <Text className="text-xs">
              Quanto do braço de teste REALMENTE viu a ficha. Sem isto um efeito nulo é ambíguo (a
              ferramenta não funciona × ninguém abriu).
            </Text>
            <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-5">
              {[
                { label: 'Orgs que abriram a ficha', valor: fmtInt(exp?.orgs_teste_com_ficha_aberta) },
                { label: '% do grupo teste', valor: fmtPct(exp?.pct) },
                { label: 'Fichas abertas', valor: fmtInt(exp?.fichas_abertas) },
                { label: 'Edições salvas', valor: fmtInt(exp?.edicoes_salvas) },
                { label: 'Recálculos via ficha', valor: fmtInt(exp?.recalcs_via_ficha) },
              ].map((item) => (
                <div key={item.label}>
                  <div className="text-[11px] text-slate-400">{item.label}</div>
                  <div className="text-lg font-semibold tabular-nums text-slate-100">
                    {item.valor}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* ── 3. Blocos: elegíveis (primário) × ITT diluído (secundário) ── */}
          {blocos.length > 0 ? (
            <div className="space-y-8">
              {blocos.map((b) => (
                <BlocoAb
                  key={b.id}
                  bloco={b}
                  qLimiar={qLimiar}
                  notaBh={data?.bh?.nota ?? null}
                  cuped={data?.cuped}
                />
              ))}
            </div>
          ) : (
            /* Payload antigo (backend sem blocos): a tabela única de sempre. */
            <Card>
              <Title className="text-base">Variáveis</Title>
              <TabelaVariaveis vars={data?.variaveis ?? []} qLimiar={qLimiar} />
              <Text className="mt-2 text-[11px]">n pequeno: leia o intervalo, não a diferença</Text>
            </Card>
          )}

          {/* ── 4. Por org ── */}
          <Card>
            <div className="flex flex-wrap items-center gap-3">
              <Title className="mr-auto text-base">
                Por org{' '}
                <span className="text-xs font-normal text-slate-500">
                  ({linhas.length} de {porOrg.length})
                </span>
              </Title>
              <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5 text-xs">
                {(['todos', 'teste', 'controle'] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setFiltroGrupo(g)}
                    className={`rounded px-2.5 py-1 capitalize transition ${
                      filtroGrupo === g
                        ? 'bg-blue-600/30 text-blue-200'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="buscar org"
                  className="w-44 rounded-md border border-slate-700 bg-slate-900 py-1.5 pl-7 pr-2 text-sm text-slate-200 placeholder:text-slate-500"
                />
              </div>
              <button
                type="button"
                onClick={exportarCsv}
                disabled={baixandoCsv}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 transition hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50"
              >
                {baixandoCsv ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Exportar CSV
              </button>
            </div>
            {erroCsv && <Text className="mt-2 text-xs text-red-300">{erroCsv}</Text>}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wide text-slate-400">
                    {ORG_COLS.map((col) => (
                      <th
                        key={String(col.key)}
                        onClick={() => ordenarPor(col.key)}
                        className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-medium hover:text-slate-200 ${
                          ehColunaTexto(col) ? 'text-left' : 'text-right'
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          <ArrowUpDown
                            className={`h-3 w-3 ${
                              sortKey === col.key ? 'text-slate-300' : 'text-slate-600'
                            }`}
                          />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr
                      key={l.org_id}
                      className="border-b border-slate-900 text-slate-300 hover:bg-slate-800/30"
                    >
                      {ORG_COLS.map((col) => (
                        <td
                          key={String(col.key)}
                          className={`whitespace-nowrap px-3 py-1.5 ${
                            ehColunaTexto(col) ? 'text-left' : 'text-right tabular-nums'
                          }`}
                          title={col.key === 'nome' ? `org_id ${l.org_id}` : undefined}
                        >
                          {celulaOrg(l, col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {linhas.length === 0 && (
                    <tr>
                      <td
                        colSpan={ORG_COLS.length}
                        className="py-6 text-center text-sm text-slate-500"
                      >
                        Nenhuma org no filtro atual.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
